import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon, CheckCircleIcon, AlertCircleIcon, CopyIcon, ExternalLinkIcon } from 'lucide-react';
import { Task } from '../components/TaskCard';
import { Customer } from '../components/CustomerCard';
import { Button } from '../components/Button';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
interface CalendarProps {
  tasks: Task[];
  customers: Customer[];
}
type ViewMode = 'day' | '3-days' | 'week' | 'month';
export function Calendar({
  tasks,
  customers
}: CalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [syncStatus, setSyncStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [syncMessage, setSyncMessage] = useState('');
  const [webcalUrl, setWebcalUrl] = useState<string>('');
  const [showWebcalUrl, setShowWebcalUrl] = useState(false);
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);
  // Helper function to get customer by ID
  const getCustomerById = (customerId: string) => {
    return customers.find(c => c.id === customerId);
  };
  // Generate ICS content
  const generateIcsContent = () => {
    const tasksWithDates = tasks.filter(task => task.agreedPlanning || task.agreedInstallation);
    let icsContent = 'BEGIN:VCALENDAR\n';
    icsContent += 'VERSION:2.0\n';
    icsContent += 'PRODID:-//Lykke EL//Opgave Kalender//DA\n';
    icsContent += 'CALSCALE:GREGORIAN\n';
    icsContent += 'METHOD:PUBLISH\n';
    icsContent += 'X-WR-CALNAME:Lykke EL Opgaver\n';
    icsContent += 'X-WR-TIMEZONE:Europe/Copenhagen\n';
    icsContent += 'X-WR-CALDESC:Automatisk opdateret kalender med opgaver\n';
    tasksWithDates.forEach(task => {
      if (task.agreedPlanning) {
        const planningDate = new Date(task.agreedPlanning);
        const dateStr = planningDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        icsContent += 'BEGIN:VEVENT\n';
        icsContent += `UID:${task.id}-planning@lykkeel.dk\n`;
        icsContent += `DTSTAMP:${dateStr}\n`;
        icsContent += `DTSTART:${dateStr}\n`;
        icsContent += `SUMMARY:Projektering: ${task.title}\n`;
        icsContent += `DESCRIPTION:Kunde: ${task.customerName || 'Ingen kunde'}\\nKategori: ${task.category}\\nStatus: ${task.status}\n`;
        icsContent += `STATUS:${task.status === 'done' ? 'CONFIRMED' : 'TENTATIVE'}\n`;
        icsContent += 'END:VEVENT\n';
      }
      if (task.agreedInstallation) {
        const installationDate = new Date(task.agreedInstallation);
        const dateStr = installationDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        icsContent += 'BEGIN:VEVENT\n';
        icsContent += `UID:${task.id}-installation@lykkeel.dk\n`;
        icsContent += `DTSTAMP:${dateStr}\n`;
        icsContent += `DTSTART:${dateStr}\n`;
        icsContent += `SUMMARY:Installation: ${task.title}\n`;
        icsContent += `DESCRIPTION:Kunde: ${task.customerName || 'Ingen kunde'}\\nKategori: ${task.category}\\nStatus: ${task.status}\n`;
        icsContent += `STATUS:${task.status === 'done' ? 'CONFIRMED' : 'TENTATIVE'}\n`;
        icsContent += 'END:VEVENT\n';
      }
    });
    icsContent += 'END:VCALENDAR';
    return icsContent;
  };
  // Sync calendar to Firebase Storage (silent background sync)
  const syncCalendarToFirebase = async () => {
    try {
      const icsContent = generateIcsContent();
      // Upload to Firebase Storage with fixed path
      const storageRef = ref(storage, 'calendar/opgaver-kalender.ics');
      await uploadString(storageRef, icsContent, 'raw', {
        contentType: 'text/calendar;charset=utf-8',
        cacheControl: 'no-cache, no-store, must-revalidate'
      });
      // Get download URL (this URL stays the same!)
      const downloadUrl = await getDownloadURL(storageRef);
      setWebcalUrl(downloadUrl);
      console.log('✅ Kalender synkroniseret til Firebase');
      return downloadUrl;
    } catch (error) {
      console.error('❌ Fejl ved synkronisering:', error);
      return null;
    }
  };
  // Initial sync on mount
  useEffect(() => {
    syncCalendarToFirebase();
  }, []);
  // Auto-sync when tasks change (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      syncCalendarToFirebase();
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [tasks]);
  // Handle show/hide URL with loading state
  const handleToggleWebcalUrl = async () => {
    if (!showWebcalUrl) {
      // Opening - ensure URL is ready
      if (!webcalUrl) {
        setIsLoadingUrl(true);
        const url = await syncCalendarToFirebase();
        setIsLoadingUrl(false);
        if (url) {
          setShowWebcalUrl(true);
        } else {
          setSyncStatus('error');
          setSyncMessage('Kunne ikke generere URL. Prøv igen.');
          setTimeout(() => {
            setSyncStatus('idle');
            setSyncMessage('');
          }, 3000);
        }
      } else {
        setShowWebcalUrl(true);
      }
    } else {
      // Closing
      setShowWebcalUrl(false);
    }
  };
  // Copy URL to clipboard
  const copyUrlToClipboard = () => {
    navigator.clipboard.writeText(webcalUrl);
    setSyncStatus('success');
    setSyncMessage('URL kopieret! Indsæt den i Outlook under "Abonner fra internettet"');
    setTimeout(() => {
      setSyncStatus('idle');
      setSyncMessage('');
    }, 3000);
  };
  // Navigate periods
  const navigatePrevious = () => {
    if (viewMode === 'day') {
      setCurrentDate(new Date(year, month, day - 1));
    } else if (viewMode === '3-days') {
      setCurrentDate(new Date(year, month, day - 3));
    } else if (viewMode === 'week') {
      setCurrentDate(new Date(year, month, day - 7));
    } else {
      setCurrentDate(new Date(year, month - 1, 1));
    }
  };
  const navigateNext = () => {
    if (viewMode === 'day') {
      setCurrentDate(new Date(year, month, day + 1));
    } else if (viewMode === '3-days') {
      setCurrentDate(new Date(year, month, day + 3));
    } else if (viewMode === 'week') {
      setCurrentDate(new Date(year, month, day + 7));
    } else {
      setCurrentDate(new Date(year, month + 1, 1));
    }
  };
  const goToToday = () => {
    setCurrentDate(new Date());
  };
  const getTasksForDate = (date: Date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const tasksForDate: Array<Task & {
      eventType: 'planning' | 'installation';
    }> = [];
    tasks.forEach(task => {
      if (task.agreedPlanning?.startsWith(dateStr)) {
        tasksForDate.push({
          ...task,
          eventType: 'planning'
        });
      }
      if (task.agreedInstallation?.startsWith(dateStr)) {
        const planningDate = task.agreedPlanning?.split('T')[0];
        const installationDate = task.agreedInstallation?.split('T')[0];
        if (planningDate !== installationDate) {
          tasksForDate.push({
            ...task,
            eventType: 'installation'
          });
        } else if (!task.agreedPlanning) {
          tasksForDate.push({
            ...task,
            eventType: 'installation'
          });
        }
      }
    });
    return tasksForDate;
  };
  const isToday = (date: Date) => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };
  const getPeriodTitle = () => {
    if (viewMode === 'day') {
      return `${dayNamesFull[currentDate.getDay()]}, ${day}. ${monthNames[month]} ${year}`;
    } else if (viewMode === '3-days') {
      const endDate = new Date(year, month, day + 2);
      return `${day}. - ${endDate.getDate()}. ${monthNames[month]} ${year}`;
    } else if (viewMode === 'week') {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(day - currentDate.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      return `Uge ${getWeekNumber(currentDate)} - ${startOfWeek.getDate()}. - ${endOfWeek.getDate()}. ${monthNames[month]} ${year}`;
    } else {
      return `${monthNames[month]} ${year}`;
    }
  };
  const getWeekNumber = (date: Date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  };
  // Render different views
  const renderDayView = () => {
    const dayTasks = getTasksForDate(currentDate);
    return <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm" data-id="element-1020">
        <div className="space-y-4" data-id="element-1021">
          {dayTasks.length > 0 ? dayTasks.map((task, index) => {
          const isPlanningEvent = task.eventType === 'planning';
          const customer = getCustomerById(task.customerId);
          const time = isPlanningEvent ? task.agreedPlanning ? new Date(task.agreedPlanning).toLocaleTimeString('da-DK', {
            hour: '2-digit',
            minute: '2-digit'
          }) : null : task.agreedInstallation ? new Date(task.agreedInstallation).toLocaleTimeString('da-DK', {
            hour: '2-digit',
            minute: '2-digit'
          }) : null;
          return <motion.div key={`${task.id}-${task.eventType}`} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: index * 0.1
          }} className="p-4 rounded-lg border border-stone-200 hover:border-blue-200 transition-colors" data-id="element-1022">
                  <div className="flex items-start justify-between mb-2" data-id="element-1023">
                    <div className="flex-1" data-id="element-1024">
                      <div className="flex items-center gap-2 mb-1" data-id="element-1025">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${isPlanningEvent ? 'bg-purple-100 text-purple-700' : 'bg-teal-100 text-teal-700'}`} data-id="element-1026">
                          {isPlanningEvent ? 'Projektering' : 'Installation'}
                        </span>
                        {time && <span className="text-sm font-semibold text-stone-900" data-id="element-1027">
                            {time}
                          </span>}
                        {customer && <span className="text-xs font-medium text-stone-500 bg-stone-100 px-2 py-0.5 rounded-full border border-stone-200" data-id="element-1028">
                            {customer.customerId}
                          </span>}
                      </div>
                      <h4 className="font-medium text-stone-900" data-id="element-1029">
                        {task.title}
                      </h4>
                      {task.customerName && <p className="text-sm text-stone-500" data-id="element-1030">
                          {task.customerName}
                        </p>}
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${task.status === 'done' ? 'bg-emerald-100 text-emerald-700' : task.status === 'in-progress' ? 'bg-amber-100 text-amber-700' : 'bg-stone-100 text-stone-700'}`} data-id="element-1031">
                      {task.status === 'done' ? 'Afsluttet' : task.status === 'in-progress' ? 'I gang' : 'Afventende'}
                    </span>
                  </div>
                </motion.div>;
        }) : <div className="text-center py-12" data-id="element-1032">
              <CalendarIcon className="w-12 h-12 text-stone-300 mx-auto mb-4" data-id="element-1033" />
              <p className="text-stone-500" data-id="element-1034">Ingen opgaver denne dag</p>
            </div>}
        </div>
      </div>;
  };
  const render3DaysView = () => {
    const days = [];
    for (let i = 0; i < 3; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + i);
      days.push(date);
    }
    return <div className="grid grid-cols-3 gap-4" data-id="element-1035">
        {days.map((date, index) => {
        const dayTasks = getTasksForDate(date);
        const today = isToday(date);
        return <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} className={`bg-white rounded-2xl p-4 border shadow-sm ${today ? 'border-teal-500 ring-2 ring-teal-200' : 'border-stone-200'}`} data-id="element-1036">
              <h3 className={`text-lg font-semibold mb-4 ${today ? 'text-teal-700' : 'text-stone-900'}`} data-id="element-1037">
                {dayNamesFull[date.getDay()]}, {date.getDate()}.{' '}
                {monthNames[date.getMonth()]}
              </h3>
              <div className="space-y-3" data-id="element-1038">
                {dayTasks.length > 0 ? dayTasks.map(task => {
              const isPlanningEvent = task.eventType === 'planning';
              const customer = getCustomerById(task.customerId);
              const time = isPlanningEvent ? task.agreedPlanning ? new Date(task.agreedPlanning).toLocaleTimeString('da-DK', {
                hour: '2-digit',
                minute: '2-digit'
              }) : null : task.agreedInstallation ? new Date(task.agreedInstallation).toLocaleTimeString('da-DK', {
                hour: '2-digit',
                minute: '2-digit'
              }) : null;
              return <div key={`${task.id}-${task.eventType}`} className="p-3 rounded-lg bg-blue-50 border border-blue-200" data-id="element-1039">
                        <div className="flex items-center gap-2 mb-1" data-id="element-1040">
                          <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${isPlanningEvent ? 'bg-purple-100 text-purple-700' : 'bg-teal-100 text-teal-700'}`} data-id="element-1041">
                            {isPlanningEvent ? 'Proj' : 'Inst'}
                          </span>
                          {time && <span className="text-xs font-semibold text-blue-900" data-id="element-1042">
                              {time}
                            </span>}
                          {customer && <span className="text-[10px] font-medium text-stone-600 bg-white px-1.5 py-0.5 rounded border border-stone-200" data-id="element-1043">
                              {customer.customerId}
                            </span>}
                        </div>
                        <p className="font-medium text-sm text-blue-900" data-id="element-1044">
                          {task.title}
                        </p>
                        {task.customerName && <p className="text-xs text-blue-700 mt-1" data-id="element-1045">
                            {task.customerName}
                          </p>}
                      </div>;
            }) : <p className="text-sm text-stone-400 text-center py-4" data-id="element-1046">
                    Ingen opgaver
                  </p>}
              </div>
            </motion.div>;
      })}
      </div>;
  };
  const renderWeekView = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      days.push(date);
    }
    return <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm" data-id="element-1047">
        <div className="grid grid-cols-7 gap-2" data-id="element-1048">
          {days.map((date, index) => {
          const dayTasks = getTasksForDate(date);
          const today = isToday(date);
          return <motion.div key={index} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: index * 0.05
          }} className={`p-3 rounded-lg border ${today ? 'bg-teal-50 border-teal-500 ring-2 ring-teal-200' : dayTasks.length > 0 ? 'bg-blue-50 border-blue-200' : 'bg-white border-stone-200'}`} data-id="element-1049">
                <div className="text-center mb-2" data-id="element-1050">
                  <p className="text-xs font-medium text-stone-500" data-id="element-1051">
                    {dayNames[date.getDay()]}
                  </p>
                  <p className={`text-lg font-bold ${today ? 'text-teal-700' : dayTasks.length > 0 ? 'text-blue-700' : 'text-stone-900'}`} data-id="element-1052">
                    {date.getDate()}
                  </p>
                </div>
                <div className="space-y-1.5" data-id="element-1053">
                  {dayTasks.slice(0, 2).map(task => {
                const isPlanningEvent = task.eventType === 'planning';
                const customer = getCustomerById(task.customerId);
                const time = isPlanningEvent ? task.agreedPlanning ? new Date(task.agreedPlanning).toLocaleTimeString('da-DK', {
                  hour: '2-digit',
                  minute: '2-digit'
                }) : null : task.agreedInstallation ? new Date(task.agreedInstallation).toLocaleTimeString('da-DK', {
                  hour: '2-digit',
                  minute: '2-digit'
                }) : null;
                return <div key={`${task.id}-${task.eventType}`} className="text-xs bg-blue-100 px-2 py-1.5 rounded" title={`${isPlanningEvent ? 'Projektering' : 'Installation'}: ${task.title}${customer ? ` - ${customer.customerId}` : ''} - ${time || ''}`} data-id="element-1054">
                        <div className="flex items-center gap-1 mb-0.5" data-id="element-1055">
                          <span className={`w-1.5 h-1.5 rounded-full ${isPlanningEvent ? 'bg-purple-600' : 'bg-teal-600'}`} data-id="element-1056" />
                          {time && <span className="font-semibold text-blue-900" data-id="element-1057">
                              {time}
                            </span>}
                          {customer && <span className="text-[9px] font-medium text-stone-600 ml-auto" data-id="element-1058">
                              {customer.customerId}
                            </span>}
                        </div>
                        <p className="font-medium text-blue-900 truncate" data-id="element-1059">
                          {task.title}
                        </p>
                      </div>;
              })}
                  {dayTasks.length > 2 && <div className="text-xs font-medium text-stone-500 text-center" data-id="element-1060">
                      +{dayTasks.length - 2} mere
                    </div>}
                </div>
              </motion.div>;
        })}
        </div>
      </div>;
  };
  const renderMonthView = () => {
    const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const calendarDays = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      calendarDays.push(null);
    }
    for (let d = 1; d <= daysInMonth; d++) {
      calendarDays.push(d);
    }
    return <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm" data-id="element-1061">
        <div className="grid grid-cols-7 gap-2 mb-2" data-id="element-1062">
          {dayNames.map(dayName => <div key={dayName} className="text-center text-sm font-semibold text-stone-600 py-2" data-id="element-1063">
              {dayName}
            </div>)}
        </div>

        <div className="grid grid-cols-7 gap-2" data-id="element-1064">
          {calendarDays.map((d, index) => {
          if (d === null) {
            return <div key={`empty-${index}`} className="aspect-square" data-id="element-1065" />;
          }
          const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), d);
          const dayTasks = getTasksForDate(date);
          const today = isToday(date);
          return <motion.div key={d} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: index * 0.01
          }} className={`aspect-square p-2 rounded-lg border transition-all ${today ? 'bg-teal-50 border-teal-500 ring-2 ring-teal-200' : dayTasks.length > 0 ? 'bg-blue-50 border-blue-200 hover:bg-blue-100' : 'bg-white border-stone-200 hover:bg-stone-50'}`} data-id="element-1066">
                <div className="flex flex-col h-full" data-id="element-1067">
                  <span className={`text-sm font-semibold ${today ? 'text-teal-700' : dayTasks.length > 0 ? 'text-blue-700' : 'text-stone-900'}`} data-id="element-1068">
                    {d}
                  </span>
                  {dayTasks.length > 0 && <div className="mt-1 space-y-1 overflow-hidden" data-id="element-1069">
                      {dayTasks.slice(0, 2).map(task => {
                  const isPlanningEvent = task.eventType === 'planning';
                  const customer = getCustomerById(task.customerId);
                  const time = isPlanningEvent ? task.agreedPlanning ? new Date(task.agreedPlanning).toLocaleTimeString('da-DK', {
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : null : task.agreedInstallation ? new Date(task.agreedInstallation).toLocaleTimeString('da-DK', {
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : null;
                  return <div key={`${task.id}-${task.eventType}`} className="text-xs bg-blue-100 px-1.5 py-1 rounded" title={`${isPlanningEvent ? 'Projektering' : 'Installation'}: ${task.title}${customer ? ` - ${customer.customerId}` : ''} - ${time || ''}`} data-id="element-1070">
                            <div className="flex items-center gap-1" data-id="element-1071">
                              <span className={`w-1 h-1 rounded-full ${isPlanningEvent ? 'bg-purple-600' : 'bg-teal-600'}`} data-id="element-1072" />
                              {time && <span className="font-semibold text-blue-900" data-id="element-1073">
                                  {time}
                                </span>}
                              {customer && <span className="text-[9px] font-medium text-stone-600 ml-auto" data-id="element-1074">
                                  {customer.customerId}
                                </span>}
                            </div>
                            <p className="font-medium text-blue-900 truncate" data-id="element-1075">
                              {task.title}
                            </p>
                          </div>;
                })}
                      {dayTasks.length > 2 && <div className="text-xs font-medium text-stone-500" data-id="element-1076">
                          +{dayTasks.length - 2} mere
                        </div>}
                    </div>}
                </div>
              </motion.div>;
        })}
        </div>
      </div>;
  };
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const day = currentDate.getDate();
  const monthNames = ['Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'December'];
  const dayNames = ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'];
  const dayNamesFull = ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'];
  return <div className="space-y-6" data-id="element-1077">
      <header className="flex items-center justify-between" data-id="element-1078">
        <div data-id="element-1079">
          <h1 className="text-4xl font-bold text-stone-900 mb-2" data-id="element-1080">Kalender</h1>
          <p className="text-stone-600" data-id="element-1081">
            Automatisk synkronisering med Outlook
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-1082">
          <Button onClick={handleToggleWebcalUrl} variant="secondary" className="flex items-center gap-2" disabled={isLoadingUrl} data-id="element-1083">
            <ExternalLinkIcon className="w-5 h-5" data-id="element-1084" />
            {isLoadingUrl ? 'Genererer...' : showWebcalUrl ? 'Skjul URL' : 'Vis abonnement URL'}
          </Button>
        </div>
      </header>

      {/* Webcal URL Display */}
      <AnimatePresence data-id="element-1085">
        {showWebcalUrl && webcalUrl && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="bg-blue-50 border border-blue-200 rounded-lg p-6" data-id="element-1086">
            <h3 className="text-lg font-semibold text-blue-900 mb-3" data-id="element-1087">
              📅 Outlook Kalender Abonnement
            </h3>
            <p className="text-sm text-blue-700 mb-4" data-id="element-1088">
              Kopiér URL'en nedenfor og indsæt den i Outlook under:
              <br data-id="element-1089" />
              <strong data-id="element-1090">Tilføj kalender → Abonner fra internettet</strong>
            </p>
            <div className="flex items-center gap-2" data-id="element-1091">
              <input type="text" value={webcalUrl} readOnly className="flex-1 px-4 py-2 bg-white border border-blue-300 rounded-lg text-sm font-mono text-blue-900" data-id="element-1092" />
              <Button onClick={copyUrlToClipboard} variant="secondary" data-id="element-1093">
                <CopyIcon className="w-4 h-4" data-id="element-1094" />
              </Button>
            </div>
            <p className="text-xs text-blue-600 mt-3" data-id="element-1095">
              ✨ Kalenderen opdateres automatisk når du tilføjer, redigerer
              eller sletter opgaver!
            </p>
          </motion.div>}
      </AnimatePresence>

      {/* Sync Status Message */}
      <AnimatePresence data-id="element-1096">
        {syncStatus !== 'idle' && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className={`p-4 rounded-lg border flex items-center gap-3 ${syncStatus === 'success' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-id="element-1097">
            {syncStatus === 'success' ? <CheckCircleIcon className="w-5 h-5 text-emerald-600 flex-shrink-0" data-id="element-1098" /> : <AlertCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0" data-id="element-1099" />}
            <div className="flex-1" data-id="element-1100">
              <p className={`text-sm font-medium ${syncStatus === 'success' ? 'text-emerald-900' : 'text-red-900'}`} data-id="element-1101">
                {syncMessage}
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* View Controls */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm" data-id="element-1102">
        <div className="flex items-center justify-between mb-6" data-id="element-1103">
          <h2 className="text-2xl font-bold text-stone-900" data-id="element-1104">
            {getPeriodTitle()}
          </h2>
          <div className="flex items-center gap-2" data-id="element-1105">
            <button onClick={goToToday} className="px-4 py-2 text-sm font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors" data-id="element-1106">
              I dag
            </button>
            <button onClick={navigatePrevious} className="p-2 text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-lg transition-all" aria-label="Forrige" data-id="element-1107">
              <ChevronLeftIcon className="w-5 h-5" data-id="element-1108" />
            </button>
            <button onClick={navigateNext} className="p-2 text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-lg transition-all" aria-label="Næste" data-id="element-1109">
              <ChevronRightIcon className="w-5 h-5" data-id="element-1110" />
            </button>
          </div>
        </div>

        {/* View Mode Selector */}
        <div className="flex gap-2 mb-6" data-id="element-1111">
          {(['day', '3-days', 'week', 'month'] as ViewMode[]).map(mode => <button key={mode} onClick={() => setViewMode(mode)} className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${viewMode === mode ? 'bg-teal-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1112">
              {mode === 'day' && 'Dag'}
              {mode === '3-days' && '3 Dage'}
              {mode === 'week' && 'Uge'}
              {mode === 'month' && 'Måned'}
            </button>)}
        </div>

        {/* Render selected view */}
        {viewMode === 'day' && renderDayView()}
        {viewMode === '3-days' && render3DaysView()}
        {viewMode === 'week' && renderWeekView()}
        {viewMode === 'month' && renderMonthView()}
      </motion.div>
    </div>;
}